"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, useMemo, startTransition, useOptimistic, Suspense, use } from 'react';
import { type DirectListing } from '@chewy/typescript-config/marketplace-types';
import { useDirectListings } from '@/hooks/marketplace/useDirectListings';
import { useConnection } from 'wagmi';
import { readContract } from 'wagmi/actions';
import { useRouter, useSearchParams } from 'next/navigation';
import NFTCard from './NFTCard';
import { erc721Abi } from 'viem';
import { getNativeCurrency, getTokensByChainId } from '@/constants/marketplace/tokens';
import { formatUnits } from 'viem';
import { config } from '@/config/index';
import { Address } from 'viem';
import { chainSettings, ChainType } from '@/constants/chain-specific';
import { getNFTMetadata } from '@/util/nftMetadata';
import { getCollectionByAddress } from '@/constants/marketplace/collections';
import { Pagination, Card, Skeleton } from '@heroui/react';
import { getOptimizedImageUrl } from '@/util/ipfs';
interface NFTMetadata {
  name?: string;
  description?: string;
  image?: string;
}

// Pagination constants
const ITEMS_PER_PAGE = 12;

// Grid skeleton component to maintain consistent layout
const GridSkeleton = t0 => {
  const $ = _c(2);
  const {
    count: t1
  } = t0;
  const count = t1 === undefined ? ITEMS_PER_PAGE : t1;
  let t2;
  if ($[0] !== count) {
    t2 = <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">{Array.from({
        length: count
      }).map(_temp)}</div>;
    $[0] = count;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};

// Component that uses React 19's use() hook to prevent layout shift
const PageContent = t0 => {
  const $ = _c(7);
  const {
    pageDataPromise,
    currentPage
  } = t0;
  const pageData = use(pageDataPromise);
  let t1;
  if ($[0] !== currentPage || $[1] !== pageData) {
    let t2;
    if ($[3] !== currentPage) {
      t2 = (nft, index) => <NFTCard key={`${nft.id}-page-${currentPage}-idx-${index}`} nft={nft} />;
      $[3] = currentPage;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = pageData.map(t2);
    $[0] = currentPage;
    $[1] = pageData;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

// Define the NFTCardProps type to match the NFTCard component's expectations
type NFTCardData = {
  id: string;
  name: string;
  description: string;
  image: string;
  owner: string;
  tokenId: string;
  assetContractAddress: string;
  collectionName: string;
  chainId: number;
  listing?: {
    formattedPrice: string;
    currencySymbol: string;
    currencyAddress?: Address;
    currencyLogo?: string;
    endsAt?: string;
  };
};
interface DirectListingsProps {
  chainId: number;
  collectionAddress?: string;
}
export default function DirectListings({
  chainId,
  collectionAddress
}: DirectListingsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get initial page from URL parameters
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const [nftData, setNftData] = useState<Map<number, NFTCardData[]>>(new Map());
  const [pagePromises, setPagePromises] = useState<Map<string, Promise<NFTCardData[]>>>(new Map());
  const [isLoadingNFTs, setIsLoadingNFTs] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [optimisticPage, setOptimisticPage] = useOptimistic(currentPage);
  const [preloadedPages, setPreloadedPages] = useState<Set<number>>(new Set());
  const {
    address
  } = useConnection();
  const {
    listings,
    loading,
    error
  } = useDirectListings(chainId);
  const nativeSymbol = chainSettings[chainId as ChainType]?.nativeCurrency.symbol || 'ETH';

  // Filter listings by collection address if provided
  const filteredListings = useMemo(() => {
    const filtered = collectionAddress ? listings.filter(listing => listing.assetContract.toLowerCase() === collectionAddress.toLowerCase()) : listings.sort((a, b) => b.listingId > a.listingId ? 1 : -1);
    return filtered;
  }, [listings, collectionAddress]);

  // Create a stable key for the filtered listings to detect changes
  const filteredListingsKey = useMemo(() => {
    return `${filteredListings.length}-${filteredListings.map(l => l.listingId).join(',').slice(0, 100)}-${collectionAddress || 'all'}`;
  }, [filteredListings, collectionAddress]);

  // Use optimistic page for immediate UI updates
  const displayPage = optimisticPage;

  // Calculate pagination
  const totalPages = Math.ceil(filteredListings.length / ITEMS_PER_PAGE);
  const startIndex = (displayPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageListings = filteredListings.slice(startIndex, endIndex);

  // Function to fetch NFT data for a specific page
  const fetchPageData = useCallback(async (page: number, pageListings: DirectListing[]) => {
    if (pageListings.length === 0) return [];
    try {
      const nftPromises = pageListings.map(async listing_0 => {
        // Get token currency info
        const supportedTokens = getTokensByChainId(chainId);
        const currencyInfo = supportedTokens?.tokens.find(token => token.address.toLowerCase() === listing_0.currency.toLowerCase()) || getNativeCurrency(chainId);

        // Get NFT metadata using proper metadata fetching system
        try {
          // Get collection info to determine token type
          const collection = getCollectionByAddress(listing_0.assetContract as Address, chainId);

          // Use tokenType from marketplace listing data (0 = ERC721, 1 = ERC1155)
          // This is more reliable than collection config since it comes from the contract
          const isERC1155 = listing_0.tokenType === 1;

          // Use the proper metadata fetching utility
          const metadata = await getNFTMetadata(listing_0.assetContract as Address, listing_0.tokenId, chainId, {
            useCache: true,
            skipCollectionConfig: false,
            isERC1155
          });

          // Get collection name
          const name = collection?.name || (await readContract(config, {
            address: listing_0.assetContract as Address,
            abi: erc721Abi,
            functionName: 'name',
            args: []
          }));

          // Format price based on token decimals
          const decimals = currencyInfo?.decimals || 18;
          const formattedPrice = formatUnits(listing_0.pricePerToken, decimals);

          // Format end timestamp to a readable date if available
          let endsAt = undefined;
          if (listing_0.endTimestamp) {
            // Convert BigInt timestamp to Date
            const endDate = new Date(Number(listing_0.endTimestamp) * 1000);
            // Format the date for display
            endsAt = endDate.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          }
          return {
            id: `${listing_0.assetContract}-${listing_0.tokenId}-${listing_0.listingId}-${listing_0.listingCreator}-${page}`,
            name: metadata.name || `${name} #${listing_0.tokenId}`,
            description: metadata.description || '',
            image: getOptimizedImageUrl(metadata.image || '', 256, 256) || '/placeholder.png',
            owner: listing_0.listingCreator,
            tokenId: String(listing_0.tokenId),
            // Convert to string to match NFTCard props
            assetContractAddress: listing_0.assetContract,
            collectionName: String(name) || 'Unknown Collection',
            chainId,
            listing: {
              formattedPrice,
              currencySymbol: currencyInfo?.symbol || nativeSymbol,
              currencyAddress: listing_0.currency as Address,
              currencyLogo: currencyInfo?.logo || '',
              endsAt
            }
          } as NFTCardData;
        } catch (err_0) {
          console.error(`Error fetching data for token ${listing_0.tokenId}:`, err_0);
          return null;
        }
      });
      const results = await Promise.all(nftPromises);
      return results.filter(Boolean) as NFTCardData[];
    } catch (err) {
      console.error('Error fetching NFT data:', err);
      return [];
    }
  }, [chainId, nativeSymbol]);

  // Function to get or create a page data promise (prevents layout shift)
  const getPageDataPromise = useCallback((page_0: number, pageListings_0: DirectListing[]): Promise<NFTCardData[]> => {
    const cacheKey = `page-${page_0}-${pageListings_0.length}`;

    // Return existing promise if available
    if (pagePromises.has(cacheKey)) {
      return pagePromises.get(cacheKey)!;
    }

    // Return cached data as resolved promise if available
    if (nftData.has(page_0)) {
      const resolvedPromise = Promise.resolve(nftData.get(page_0)!);
      setPagePromises(prev => new Map(prev).set(cacheKey, resolvedPromise));
      return resolvedPromise;
    }

    // Create new promise for fetching data
    const dataPromise = fetchPageData(page_0, pageListings_0);
    setPagePromises(prev_0 => new Map(prev_0).set(cacheKey, dataPromise));

    // Cache the result when promise resolves
    dataPromise.then(result => {
      setNftData(prev_1 => new Map(prev_1).set(page_0, result));
    });
    return dataPromise;
  }, [pagePromises, nftData, fetchPageData]);

  // Reset pagination when filtered listings change
  useEffect(() => {
    setNftData(new Map());
    setPreloadedPages(new Set());
    setCurrentPage(1);
  }, [filteredListingsKey]);

  // Load current page data
  useEffect(() => {
    const loadCurrentPage = async () => {
      if (preloadedPages.has(currentPage) || currentPageListings.length === 0) return;
      console.log(`DirectListings: Loading page ${currentPage} with ${currentPageListings.length} listings`);
      setIsLoadingNFTs(true);
      try {
        const pageData = await fetchPageData(currentPage, currentPageListings);
        console.log(`DirectListings: Loaded ${pageData.length} NFTs for page ${currentPage}`);
        setNftData(prev => new Map(prev).set(currentPage, pageData));
        setPreloadedPages(prev => new Set(prev).add(currentPage));
      } finally {
        setIsLoadingNFTs(false);
      }
    };
    loadCurrentPage();
  }, [currentPage, currentPageListings, fetchPageData, preloadedPages]);

  // Preload next page
  useEffect(() => {
    const preloadNextPage = async () => {
      const nextPage = currentPage + 1;
      if (nextPage > totalPages || preloadedPages.has(nextPage)) return;
      const nextStartIndex = (nextPage - 1) * ITEMS_PER_PAGE;
      const nextEndIndex = nextStartIndex + ITEMS_PER_PAGE;
      const nextPageListings = filteredListings.slice(nextStartIndex, nextEndIndex);
      if (nextPageListings.length === 0) return;
      try {
        const pageData = await fetchPageData(nextPage, nextPageListings);
        setNftData(prev_2 => new Map(prev_2).set(nextPage, pageData));
        setPreloadedPages(prev_3 => new Set(prev_3).add(nextPage));
      } catch (err_1) {
        console.error('Error preloading next page:', err_1);
      }
    };

    // Preload after a short delay to prioritize current page
    const timeoutId = setTimeout(preloadNextPage, 500);
    return () => clearTimeout(timeoutId);
  }, [currentPage, totalPages, filteredListings, fetchPageData, preloadedPages]);

  // Function to update URL parameters
  const updateUrlParams = useCallback((page_1: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (page_1 === 1) {
      params.delete('page'); // Remove default page from URL
    } else {
      params.set('page', page_1.toString());
    }
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    router.push(newUrl, {
      scroll: false
    });
  }, [searchParams, router]);

  // Handle page change with React 19 optimistic updates
  const handlePageChange = useCallback((page_2: number) => {
    // Immediately update optimistic state (prevents flash)
    setOptimisticPage(page_2);

    // Update actual state and URL in a transition
    startTransition(() => {
      setCurrentPage(page_2);
      updateUrlParams(page_2);
    });
  }, [updateUrlParams, setOptimisticPage]);

  // Sync state with URL parameters when they change (browser back/forward)
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
      setOptimisticPage(urlPage);
    }
  }, [searchParams, currentPage, setOptimisticPage]);

  // Get current page data
  const currentPageData = nftData.get(currentPage) || [];

  // Generate placeholder for loading state
  const generatePlaceholder = (id: string): NFTCardData => ({
    id,
    name: '',
    description: '',
    image: '',
    owner: '',
    tokenId: '',
    assetContractAddress: '',
    collectionName: '',
    chainId: 0
  });
  if (loading) {
    return <div className="py-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {Array.from({
          length: ITEMS_PER_PAGE
        }, (_, i) => <Card key={i} className="overflow-hidden">
              <Skeleton className="h-60 w-full" />
              <div className="p-4">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </Card>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="py-4 text-center text-red-500">
        Error loading listings: {error.message}
      </div>;
  }
  if (!loading && filteredListings.length === 0) {
    return <div className="py-12 text-center text-gray-400">
        <p>No active listings found</p>
      </div>;
  }
  return <div className="py-4 space-y-6">
      {/* NFT Grid with Suspense for layout shift prevention */}
      <Suspense fallback={<GridSkeleton count={ITEMS_PER_PAGE} />}>
        <PageContent pageDataPromise={getPageDataPromise(displayPage, currentPageListings)} currentPage={displayPage} />
      </Suspense>

      {/* Pagination */}
      {totalPages > 1 && <div className="flex justify-center">
          <Pagination total={totalPages} page={displayPage} onChange={handlePageChange} showControls size="sm" classNames={{
        wrapper: "gap-1 overflow-visible",
        item: "w-8 h-8 text-small rounded-md bg-default-100/50 hover:bg-default-200",
        cursor: "bg-primary text-primary-foreground font-bold shadow-md"
      }} />
        </div>}

      {/* Show total count */}
      <div className="flex justify-center">
        <div className="text-xs text-default-500 bg-default-50 px-3 py-2 rounded-full">
          Showing {(displayPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(displayPage * ITEMS_PER_PAGE, filteredListings.length)} of {filteredListings.length} listings
        </div>
      </div>
    </div>;
}
function _temp(_, i) {
  return <Card key={i} className="overflow-hidden"><Skeleton className="h-60 w-full" /><div className="p-4"><Skeleton className="h-6 w-3/4 mb-2" /><Skeleton className="h-4 w-1/2" /></div></Card>;
}